import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  AppDispatch,
  RootState,
} from "../../../../store/store";

import useLoading from "../../../../hooks/useLoading";
import useApiErrorHandler from "../../../../hooks/useApiErrorHandler";
import useModals from "../../../../hooks/useModals";

import {
  updatePaymentSummary,
  resetPaymentDetails,
  resetPayablePaymentForm,
} from "../store/PayablePayment.slice";

import PayablePaymentService from "../services/PayablePayment.service";
import { invalidateCashQueries } from '../../../../features/cash/invalidateCashQueries';

export const useApplyPayment = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const { handle } = useApiErrorHandler();
  const { openModal } = useModals();

  const {
    showLoadingIndicator,
    hideLoadingIndicator,
  } = useLoading();

  /**
   * 🔥 leer estado real desde redux
   */
  const {
    summary,
    paymentDate,
    notes,
    payments,
  } = useSelector(
    (state: RootState) => state.payablePayment
  );

  /**
   * 💰 Aplicar pago
   */
  const applyPayment = async () => {
    const total = payments.reduce(
      (sum, payment) => sum + (Number(payment.amount) || 0),
      0
    );

    if (
      payments.length === 0 ||
      payments.some((payment) => !payment.paymentMethodId)
    ) {
      openModal('warning', {
        title: 'Método de pago requerido',
        message: 'Seleccione al menos un método de pago.',
      });
      return null;
    }

    if (
      payments.some((payment) => Number(payment.amount) <= 0)
    ) {
      openModal('warning', {
        title: 'Monto inválido',
        message: 'Cada monto debe ser mayor que cero.',
      });
      return null;
    }

    if (total > Number(summary?.balance || 0)) {
      openModal('warning', {
        title: 'Monto superior al saldo',
        message: 'La suma total no puede superar el saldo pendiente.',
      });
      return null;
    }

    try {
      showLoadingIndicator();

      const payload = {
        supplierId: summary?.supplierId,
        payableId: summary?.id || null,
        paymentDate,
        notes,
        details: payments.map((p) => ({
          paymentMethodId: p.paymentMethodId,
          amount: Number(p.amount) || 0,
          reference: p.reference || null,
        })),
      };

      const response =
        await PayablePaymentService.create(payload);

      const {
        title,
        message,
        type,
        statusCode,
        data: paymentData,
      } = response;

      console.log("✅ pago registrado:", response);

      /**
       * 🔥 modal de éxito
       */
      if (statusCode === 201) {
        openModal(type, {
          title,
          message,
        });
      }

      /**
       * 🔥 actualizar summary visual
       */
      if (paymentData) {
        invalidateCashQueries(dispatch);
        dispatch(
          updatePaymentSummary({
            paidAmount: paymentData.paidAmount,
            balance: paymentData.balance,
            status: paymentData.status,
          })
        );

        /**
         * 🔥 limpiar notas + métodos de pago
         */
        dispatch(resetPaymentDetails());
      }

      /**
       * 🔥 si quedó totalmente pagado
       */
      if (paymentData?.status === "PAID") {
        dispatch(resetPayablePaymentForm());

        navigate("/finance/cxp/supplier-balances");
      }

      return paymentData;
    } catch (err: any) {
      handle(err);
      return null;
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
   * ❌ Cancelar proceso de pago
   *
   * - limpia formulario completo
   * - regresa a listado principal
   */
  const cancelPayment = () => {
    dispatch(resetPayablePaymentForm());

    navigate("/finance/cxp/supplier-balances");
  };

  return {
    // state
    summary,
    paymentDate,
    notes,
    payments,

    // actions
    applyPayment,
    cancelPayment,
  };
};
